import { ClientApi } from '@quadminds/client-apis/base';
import { AuthMeV1OutputDto, IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { AUTH_API_PATHS, REFRESH_HEADER, SKIP_COOKIES_HEADER } from '@quadminds/shared/commons';
import {
    axiosResolver,
    clearTokensFromStorage,
    getTokenFromAuthHeaders,
    isDefined,
    jwtDecode,
    validateTokenExp,
} from '@quadminds/shared/utils';
import { AxiosRequestConfig } from 'axios';
import { AUTH_API_TOKEN_NAME, DefaultAuthApiConfig, ID_TOKEN_HEADER_NAME } from './constants';
import { AuthApiConfig } from './interfaces';
import {
    Credentials,
    LoginResponse,
    RedirectUrlResponse,
    RefreshTokenResponse,
    SendRecoveryEmailResponse,
    SignupPayload,
    SignupResponse,
    VerifyEmailResponse,
} from './types';

/**
 * Client side auth API.
 */
export class AuthApi extends ClientApi {
    public useStorage = false;

    constructor({
        useStorage = false,
        useUnauthorizedInterceptor = false,
        ...rest
    }: AuthApiConfig = DefaultAuthApiConfig) {
        const config = rest;
        super({
            namespace: AUTH_API_TOKEN_NAME,
            ...config,
        });
        this.useStorage = useStorage;
        if (useUnauthorizedInterceptor) this.addAuthInterceptors();
    }

    // Private endpoints that required authentication via Bearer tokens
    public getUserInfo() {
        return axiosResolver<IAuthMeV2OutputDto>(() =>
            this.httpClient.get(AUTH_API_PATHS.V2.AUTH.ME, { headers: { ...this.getAuthHeaders() } }),
        );
    }

    public exchangeExternalToken() {
        return axiosResolver<LoginResponse>(() =>
            this.httpClient.post(
                AUTH_API_PATHS.V2.AUTH.EXCHANGE_EXTERNAL_TOKEN,
                {},
                { headers: { ...this.getAuthHeaders() }, withCredentials: true },
            ),
        );
    }

    /**
     * Exchanges the given token of another auth-service for an access token of this one.
     *
     * Unlike `exchangeExternalToken()`, the token is passed in instead of read from storage, and the
     * response carries no refresh token: the auth-service sets no cookies and leaves the user's stored
     * refresh token alone, so the sessions already open in either platform keep working. Meant for
     * apps embedded in another platform, which renew by exchanging again.
     */
    public exchangeExternalAccessToken(externalToken: string) {
        return axiosResolver<Pick<LoginResponse, 'accessToken'>>(() =>
            this.httpClient.post(
                AUTH_API_PATHS.V2.AUTH.EXCHANGE_EXTERNAL_TOKEN,
                {},
                {
                    headers: { Authorization: `Bearer ${externalToken}`, [SKIP_COOKIES_HEADER]: 'true' },
                    withCredentials: false,
                },
            ),
        );
    }

    public setCurrentOrganization(orgId: string) {
        return axiosResolver<LoginResponse>(() =>
            this.httpClient.patch(
                AUTH_API_PATHS.V2.AUTH.SELECT_ORGANIZATION,
                { organizationId: orgId },
                {
                    headers: { ...this.getAuthHeaders() },
                },
            ),
        );
    }

    public getLegacyUserInfo() {
        return axiosResolver<AuthMeV1OutputDto>(() =>
            this.httpClient.get(AUTH_API_PATHS.V1.AUTH.ME, { headers: { ...this.getAuthHeaders() } }),
        );
    }
    public verifyChangedEmail(token: string) {
        return axiosResolver<VerifyEmailResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V2.AUTH.VERIFY_EMAIL, {}, { params: { token } }),
        );
    }

    public checkSession() {
        return axiosResolver<LoginResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V1.AUTH.CHECK_SESSION, {}, { withCredentials: true }),
        );
    }

    public refreshTokenV1() {
        return axiosResolver<RefreshTokenResponse>(() =>
            this.httpClient.get(AUTH_API_PATHS.V1.AUTH.REFRESH, { withCredentials: true }),
        );
    }

    // Public endpoints
    public loginWithEmailAndPasswordV1(params: Credentials) {
        return axiosResolver<LoginResponse>(() => this.httpClient.post(AUTH_API_PATHS.V1.AUTH.LOGIN, params));
    }

    public loginWithEmailAndPasswordV2(idToken: string) {
        return axiosResolver<LoginResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V2.AUTH.LOGIN, null, {
                headers: { ...AuthApi.getIdentityProviderAuthHeaders(idToken) },
            }),
        );
    }

    public loginThroughThirdParty(idToken: string) {
        return axiosResolver<LoginResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V2.AUTH.LOGIN, null, {
                headers: { ...AuthApi.getIdentityProviderAuthHeaders(idToken) },
            }),
        );
    }

    public signup(payload: SignupPayload, invitationUuid: string) {
        return axiosResolver<SignupResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V2.AUTH.REGISTER, payload, {
                params: {
                    invitation: invitationUuid,
                },
            }),
        );
    }

    public redirect() {
        return axiosResolver<RedirectUrlResponse>(() =>
            this.httpClient.get(AUTH_API_PATHS.V2.AUTH.REDIRECT, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    public refreshToken() {
        let headers: Record<string, string> = {};
        const refresh = AuthApi.getCurrentRefreshToken();
        if (refresh) {
            headers = { [REFRESH_HEADER]: refresh };
        }
        return axiosResolver<RefreshTokenResponse>(() =>
            this.httpClient.get(AUTH_API_PATHS.V2.AUTH.REFRESH, {
                withCredentials: true,
                ...headers,
            }),
        );
    }

    public logout() {
        return axiosResolver(() => this.httpClient.post(AUTH_API_PATHS.V2.AUTH.LOGOUT, {}, { withCredentials: true }));
    }

    public sendRecoveryEmailV1(email: string) {
        return axiosResolver<SendRecoveryEmailResponse>(() =>
            this.httpClient.post(AUTH_API_PATHS.V1.AUTH.SEND_RECOVERY_EMAIL, { email }),
        );
    }

    // Utilities
    getAuthHeaders() {
        let authHeader = {};
        const token = AuthApi.getCurrentToken();
        if (token) authHeader = { Authorization: `Bearer ${token}` };
        return authHeader;
    }

    private static getIdentityProviderAuthHeaders(idToken: string) {
        const identityProviderAuthHeaders = { [`${ID_TOKEN_HEADER_NAME}`]: `${idToken}` };
        return identityProviderAuthHeaders;
    }

    private static getCurrentToken() {
        return localStorage.getItem('token');
    }

    private static getCurrentRefreshToken() {
        return localStorage.getItem('refreshToken');
    }

    // ----------------- Config methods ------------------

    // Add a response interceptor to refresh the JWT token if it is expired
    private addAuthInterceptors() {
        this.httpClient.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config as AxiosRequestConfig & { isRetry: boolean };

                if (!originalRequest.url) return Promise.reject(error);

                // Other error not 401 we can safely return error
                if (error.response.status !== 401) {
                    return Promise.reject(error);
                }

                if (error.response.status === 401 && !originalRequest.isRetry) {
                    originalRequest.isRetry = true;
                    // Reject if got unauthorized by refresh attempt
                    if (
                        [AUTH_API_PATHS.V2.AUTH.REFRESH, AUTH_API_PATHS.V1.AUTH.REFRESH].includes(originalRequest.url)
                    ) {
                        return Promise.reject(error);
                    }

                    clearTokensFromStorage();

                    // Check wether request was made using an issued token (after authentication)
                    const requestToken = getTokenFromAuthHeaders(originalRequest);

                    // Check token validity
                    const tokenObj = jwtDecode(requestToken);
                    const tokenHasPayload = Object.keys(tokenObj.payload).length;
                    const tokenStillValid = validateTokenExp(tokenObj);

                    // If previous token found, assume refresh cookie is set.
                    // Otherwise use platform session cookie
                    const hasToRefresh = tokenHasPayload && !tokenStillValid;

                    // Reject if got unauthorized by check session attempt
                    if (!hasToRefresh && [AUTH_API_PATHS.V1.AUTH.CHECK_SESSION].includes(originalRequest.url)) {
                        return Promise.reject(error);
                    }

                    try {
                        // Attempts to refresh token using auth cookies
                        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = hasToRefresh
                            ? await this.refreshToken()
                            : await this.checkSession();

                        // Flag can be false, as auth mechanism also works with http-only cookies
                        if (this.useStorage) {
                            localStorage.setItem('token', newAccessToken);
                            localStorage.setItem('refreshToken', newRefreshToken);
                        }

                        // Set default headers so it can be used by new requests
                        this.httpClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

                        if (isDefined(originalRequest.headers)) {
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                        }

                        // Re-run the original request that was intercepted
                        const originalResponse = await this.httpClient.request(originalRequest);
                        return originalResponse.data;
                    } catch (error) {
                        // Return the original error if we can't handle it
                        return Promise.reject(error);
                    }
                }
                return Promise.reject(error);
            },
        );
    }
}
